export const PROPERTY_TYPES = [
  "Apartment / Flat",
  "House",
  "Studio",
  "Airbnb / Short-term rental",
  "Long-term rental property",
  "Office",
  "Shop",
  "Restaurant",
  "Other",
];

export const TIME_SLOTS = [
  { value: "morning", label: "Morning", hint: "8:00 – 12:00" },
  { value: "afternoon", label: "Afternoon", hint: "12:00 – 16:00" },
  { value: "evening", label: "Evening", hint: "16:00 – 19:00" },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "How your space makes you feel",
    text: "Cleaning is not really about spotless surfaces. It is about how your space makes you feel - the calm of a fresh kitchen in the morning, the ease of a bedroom that is already done.",
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1400&q=80",
    alt: "A lived-in London living room with natural light, houseplants, warm textures and personal touches",
  },
  {
    n: "02",
    title: "One less thing to think about",
    text: "It is about coming home after a long day and knowing you don't have to worry about the cleaning. The list is shorter. The evening is yours.",
    image: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1400&q=80",
    alt: "Fresh, bright bathroom interior with natural light",
  },
  {
    n: "03",
    title: "Time is the real luxury",
    text: "More time for yourself, your family, your work, your friends - or simply doing nothing. At HOMELARA, we don't just clean your space. We give you time back.",
    image: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1400&q=80",
    alt: "Elegant living room with warm afternoon light and cared-for furnishings",
  },
];

export const WHY_POINTS = [
  { n: "01", title: "Time Back", text: "We take cleaning off your to-do list." },
  { n: "02", title: "Thoughtful Care", text: "We treat your space with care and attention." },
  { n: "03", title: "Professional Service", text: "Reliable cleaning with clear standards and expectations." },
  { n: "04", title: "Peace of Mind", text: "A clean space without having to spend your own time making it happen." },
];

export const HOME_STEPS = [
  { n: "1", title: "Tell us what you need", text: "Choose your service and tell us a little about your space." },
  { n: "2", title: "Choose your date", text: "Select a convenient date and time." },
  { n: "3", title: "We take care of the cleaning", text: "Our team arrives prepared and gets to work." },
  { n: "4", title: "Enjoy your time back", text: "Come back to a clean, fresh and cared-for space." },
];

export const JOURNEY_STEPS = [
  { n: "01", title: "Choose your service", text: "Home, Deep, Property or Business Cleaning - pick the one that fits, or ask us if you're unsure." },
  { n: "02", title: "Tell us about your space", text: "A few quick details: property type, bedrooms, bathrooms and anything specific we should know." },
  { n: "03", title: "Choose your preferred date and time", text: "Pick the day and time slot that suits your week." },
  { n: "04", title: "Receive confirmation", text: "See your fixed price and send your booking request. Once we've confirmed availability, we'll email you a secure payment link - your booking is confirmed when payment is complete." },
  { n: "05", title: "Our cleaner arrives prepared", text: "Professional products, a clear plan and care for your space." },
  { n: "06", title: "Enjoy your clean space", text: "Come home to a space that feels cared for - and enjoy your time back." },
];

export const TRUST_ITEMS = [
  "Professional service",
  "Clear communication",
  "Reliable standards",
  "Thoughtful attention to detail",
  "Customer-focused approach",
  "London-based",
];

export const SERVICE_DETAILS = {
  "home-cleaning": {
    includesTitle: "What's included",
    sections: [
      {
        title: "Kitchen",
        items: [
          "Worktops and surfaces cleaned",
          "Hob cleaned",
          "Sink cleaned",
          "External surfaces of appliances cleaned",
          "Cupboard fronts spot cleaned",
          "Kitchen floor vacuumed and mopped",
          "Bins emptied",
        ],
      },
      {
        title: "Bathrooms",
        items: [
          "Toilet cleaned and sanitised",
          "Sink and taps cleaned",
          "Shower and bath cleaned",
          "Mirrors cleaned",
          "Bathroom surfaces wiped",
          "Floors vacuumed and mopped",
        ],
      },
      {
        title: "Living areas & bedrooms",
        items: [
          "Dusting of accessible surfaces",
          "Furniture surfaces cleaned",
          "Beds made",
          "Floors vacuumed",
          "Hard floors mopped",
          "General tidying of accessible areas",
        ],
      },
      {
        title: "General",
        items: [
          "Cobweb removal",
          "High-touch surfaces wiped",
          "Doors and handles spot cleaned",
          "Bins emptied",
          "Overall finishing check",
        ],
      },
    ],
    note: "Decluttering, laundry, ironing, washing dishes, moving heavy furniture and internal oven or fridge cleaning are not part of a standard Home Cleaning visit - most can be added as optional extras below.",
    extrasTitle: "Optional extras",
    equipmentTitle: "Equipment",
    equipmentText:
      "For regular home cleaning, we ask that you provide a vacuum cleaner, mop and bucket. HOMELARA provides all professional cleaning products and everything else needed for your agreed clean - you don't need to supply anything else unless we specifically agree it in advance.",
  },
  "deep-cleaning": {
    deepIncludes: {
      title: "What's included",
      statement: "Deep Cleaning includes everything in our One-Off Cleaning service, plus additional detailed cleaning throughout the property.",
      linkLabel: "View what's included in One-Off Cleaning",
      linkTo: "/services/home-cleaning#whats-included",
      plusLabel: "Everything included in One-Off Cleaning, plus:",
      sections: [
        {
          title: "Kitchen",
          items: [
            "Detailed degreasing of accessible areas",
            "Detailed cleaning around appliances",
            "Inside oven included",
            "Inside fridge included",
            "Detailed attention to splashbacks",
            "Detailed attention to cupboard fronts",
          ],
        },
        {
          title: "Bathrooms",
          items: [
            "Detailed limescale attention",
            "Detailed cleaning of taps and fixtures",
            "Tiles",
            "Accessible grout attention",
            "More detailed shower and bath cleaning",
          ],
        },
        {
          title: "Living Areas & Bedrooms",
          items: [
            "Detailed dusting",
            "Skirting boards",
            "Doors and frames",
            "Light switches",
            "Accessible corners",
            "Accessible areas behind and under furniture",
            "More detailed floor cleaning",
          ],
        },
        {
          title: "Additional Areas",
          items: [
            "Internal doors",
            "Handles",
            "Accessible vents",
            "Window sills",
            "Accessible high surfaces",
            "Cobweb removal in less accessible areas",
          ],
        },
      ],
      chargesNote: "Interior windows, heavy-soiling treatment, excessive pet hair, severe mould/limescale and other specialist cleaning requirements may incur an additional charge.",
    },
    excludeExtras: ["oven", "fridge", "oven-fridge"],
    extrasTitle: "Optional extras",
    equipmentTitle: "Equipment",
    equipmentText:
      "For a deep clean, we ask that you provide a vacuum cleaner, mop and bucket. HOMELARA provides all professional cleaning products and everything else needed for your agreed clean - you don't need to supply anything else unless we specifically agree it in advance.",
  },
  "property-cleaning": {
    idealFor: [
      "Airbnb",
      "Short-term rentals",
      "Holiday lets",
      "Long-term rental properties",
      "Landlords",
      "Property managers",
      "Properties between tenants",
      "Properties being prepared for viewings",
    ],
    helpTitle: "What we can help with",
    helpWith: [
      "Full property cleaning",
      "Kitchen and bathroom cleaning",
      "Bedroom preparation",
      "Bed making",
      "Linen change",
      "Towel replacement",
      "Floors vacuumed and mopped",
      "Dusting and surface cleaning",
      "Bins emptied",
      "High-touch surfaces cleaned",
      "Restocking agreed supplies",
      "Final presentation check",
    ],
    turnover: {
      headline: "Between guests, every detail matters.",
      text: "We can prepare your property so it feels fresh, clean and ready for the next arrival.",
      items: [
        "Full turnover clean",
        "Linen and towel changes",
        "Kitchen reset",
        "Bathroom reset",
        "Bedroom preparation",
        "Restocking",
        "Final visual check",
      ],
    },
    extrasNote:
      "Optional extras include inside oven, inside fridge, inside windows, laundry, ironing, additional linen changes, restocking, end-of-tenancy cleaning, deep cleaning and specific property requests.",
    equipmentTitle: "Equipment",
    equipmentText:
      "For property and turnover cleans, we ask that the property provides a vacuum cleaner, mop and bucket. HOMELARA provides all professional cleaning products and everything else needed for the agreed service - you don't need to supply anything else unless we specifically agree it in advance.",
  },
  "business-cleaning": {
    idealFor: [
      "Offices",
      "Studios",
      "Shops",
      "Restaurants",
      "Small businesses",
      "Professional spaces",
      "Shared workspaces",
    ],
    includesTitle: "What's included",
    included: [
      "Dusting and surface cleaning",
      "Desks and accessible work surfaces",
      "Common areas",
      "Kitchens and staff areas",
      "Toilets and washrooms",
      "Floors vacuumed",
      "Hard floors mopped",
      "Bins emptied",
      "High-touch surfaces",
      "Doors and handles",
      "General presentation check",
    ],
    tailoredTitle: "Tailored to your business",
    tailoredText:
      "Every business is different, so we can adapt the cleaning routine to your space, opening hours and requirements.",
    optional: [
      "Internal windows",
      "Deep cleaning",
      "Fridge cleaning",
      "Oven cleaning",
      "Detailed kitchen cleaning",
      "Restroom deep cleaning",
      "Periodic deep cleans",
      "Other agreed cleaning requirements",
    ],
    equipmentTitle: "Equipment",
    equipmentText:
      "For business cleaning, we ask that your premises provide a vacuum cleaner, mop and bucket. HOMELARA provides all professional cleaning products and everything else needed for your agreed cleaning plan - you don't need to supply anything else unless we specifically agree it in advance.",
  },
};

export const FAQS = [
  {
    q: "What areas do you cover?",
    a: "We currently provide services across London Zones 1 and 2, with plans to expand in the future.",
  },
  {
    q: "Do I need to provide cleaning products?",
    a: "No. HOMELARA provides all professional cleaning products and everything else needed for your agreed service.",
  },
  {
    q: "Do I need to provide a vacuum cleaner?",
    a: "Yes. For every service - Home, Deep, Property and Business Cleaning - we ask that the property provides a suitable vacuum cleaner, mop and bucket. HOMELARA provides all professional cleaning products and everything else needed for the agreed service, so you don't need to supply anything else unless we specifically agree it in advance.",
  },
  {
    q: "What is the difference between Home Cleaning and Deep Cleaning?",
    a: "Home Cleaning is designed for regular maintenance and keeping your home consistently fresh. Deep Cleaning is more detailed and focuses on areas that are not normally covered during a regular clean.",
  },
  {
    q: "Can I add extra services?",
    a: "Yes. Optional extras - such as inside oven, inside fridge, inside windows, laundry or ironing - can be added depending on the service and property. You'll see them in the booking flow.",
  },
  {
    q: "Can I book a regular cleaning service?",
    a: "Yes. Many of our customers prefer a recurring arrangement - weekly, fortnightly or monthly. Mention it in your booking notes or enquiry and we'll set it up with you.",
  },
  {
    q: "Do you clean Airbnb properties?",
    a: "Yes. We provide Property Cleaning and turnover cleaning for short-term rental properties, including linen changes, resets and restocking.",
  },
  {
    q: "Do you clean offices?",
    a: "Yes. We provide Business Cleaning for offices, studios, shops, restaurants and other small businesses.",
  },
  {
    q: "Can I request a specific cleaner?",
    a: "You can mention a preferred cleaner in your booking notes and we'll do our best to accommodate the request, subject to availability.",
  },
  {
    q: "What happens if I need to reschedule?",
    a: "Life happens - just let us know as early as possible and we'll move your booking to a new date. You can read the full approach in our Cancellation Policy.",
  },
  {
    q: "What if I need to cancel?",
    a: "Please see our Cancellation Policy for full details. In short: the earlier you tell us, the easier it is, and we'll always try to be fair.",
  },
  {
    q: "Do you provide end-of-tenancy cleaning?",
    a: "Yes. End-of-tenancy cleaning can be arranged as a specific cleaning service or as an additional service depending on the property. Tell us about the property and we'll advise.",
  },
  {
    q: "Do you clean inside ovens and fridges?",
    a: "Yes - these can be added as optional extras to most services during booking.",
  },
  {
    q: "Do you do laundry and ironing?",
    a: "Yes, these can be offered as optional extras where available. Add them during booking or mention them in your enquiry.",
  },
  {
    q: "Can I request a custom clean?",
    a: "Yes. If you have specific requirements that don't fit a standard service, contact us and we'll put together something tailored.",
  },
];

export const LEGAL_CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "Last updated: July 2026",
    intro: "HOMELARA LTD respects your privacy. This policy explains what information we collect through this website and how we use it. It will be reviewed and expanded as the business grows.",
    sections: [
      {
        h: "What we collect",
        p: "When you submit a booking request or contact form, we collect the details you provide: your name, email address, phone number, address and any notes about your property or requirements.",
      },
      {
        h: "How we use it",
        p: "We use this information only to respond to your enquiry, prepare your quote, arrange and deliver your cleaning service, and communicate with you about your booking. We do not sell or share your personal information with third parties for marketing purposes.",
      },
      {
        h: "How it is stored",
        p: "Your information is stored securely in our booking system and is accessible only to authorised HOMELARA administrators. We keep it only for as long as needed to manage your bookings and enquiries.",
      },
      {
        h: "Your rights",
        p: "You can ask us at any time to view, correct or delete the personal information we hold about you by emailing lauraflorez@homelara.uk.",
      },
    ],
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    updated: "Last updated: July 2026",
    intro: "These terms govern the use of the HOMELARA website and the booking of HOMELARA LTD cleaning services. They are a working draft and will be finalised by HOMELARA LTD.",
    sections: [
      {
        h: "Bookings",
        p: "Submitting a booking through this website is a booking request. HOMELARA reviews every request and checks availability before approving it or suggesting an alternative. Once approved, you will receive a secure payment link, and your booking is confirmed only after your payment has been successfully completed and confirmed by our payment provider, Stripe. Your preferred time is a requested arrival window and is not necessarily an exact arrival time; HOMELARA will confirm your exact appointment time with you.",
      },
      {
        h: "Pricing & payment",
        p: "HOMELARA offers fixed prices per property and job. The prices shown during booking are the applicable HOMELARA prices for your selected service, property and any extras, and you will see the full total before submitting your request. No payment is taken when you submit a request; once HOMELARA approves your booking, payment is taken securely through Stripe using the link we send you. HOMELARA is currently not VAT registered, so no VAT is added.",
      },
      {
        h: "Final terms",
        p: "The final HOMELARA Terms & Conditions will be published on this page before the website goes live.",
      },
      {
        h: "The service",
        p: "We agree the scope of each clean with you in advance. What's included in each service is described on the relevant service page. Optional extras can be added at booking or by arrangement.",
      },
      {
        h: "Access & equipment",
        p: "For all services - Home, Deep, Property and Business Cleaning - we ask that the property provides a vacuum cleaner, mop and bucket. HOMELARA provides all professional cleaning products and everything else needed for the agreed service, unless something different is specifically agreed in advance.",
      },
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy",
    updated: "Last updated: July 2026",
    intro: "This page explains how the HOMELARA website uses cookies. It will be updated as the website evolves.",
    sections: [
      {
        h: "What we use",
        p: "This website uses only essential cookies required for it to function - for example, secure session cookies in the private admin area. We do not use advertising or tracking cookies.",
      },
      {
        h: "Managing cookies",
        p: "You can control or delete cookies through your browser settings at any time. The website will continue to work without non-essential cookies.",
      },
    ],
  },
  "cancellation-policy": {
    title: "Cancellation & Rescheduling Policy",
    updated: "Last updated: July 2026 - draft, to be finalised by HOMELARA LTD",
    intro: "We understand that plans change. This policy sets out a fair, simple approach to rescheduling and cancelling cleans. It is a working draft and may be updated.",
    sections: [
      {
        h: "Rescheduling",
        p: "You can reschedule your clean free of charge up to 48 hours before the scheduled start time - just contact us and we'll find a new date together. We will always try to accommodate changes made inside 48 hours, subject to availability.",
      },
      {
        h: "Cancelling",
        p: "You can cancel free of charge up to 48 hours before your clean. Cancellations made within 48 hours of the scheduled start may be subject to a cancellation fee, to be confirmed in your booking confirmation.",
      },
      {
        h: "How to reach us",
        p: "To reschedule or cancel, email lauraflorez@homelara.uk or message us on WhatsApp at +44 7939 365873.",
      },
    ],
  },
};
